// src/modules/dashboard/graphql/queries/index.ts
import { gql } from '@apollo/client';

// Note: These queries are designed to work with dynamic regions from your database.
// The exact field names and structure may need to be adjusted based on your
// actual GraphQL schema and region/location data structure.

// Updated query without period, focused on region-based analytics
export const GET_DONOR_ANALYTICS_BY_REGION_QUERY = gql`
  query getDonorAnalyticsByRegion($input: GetDonorAnalyticsByRegionInput!) {
    getDonorAnalyticsByRegion(input: $input) {
      regionId
      regionName
      totalActiveDonors
      totalSingleDonors
      totalDoubleDonors
      totalNewDonors
      totalExperiencedDonors
      statusBreakdown {
        status
        count
      }
      recentDonors {
        id
        name
        date
        days
        location
        status
      }
    }
  }
`;

// Query to get regions with their associated locations for mapping
export const GET_REGIONS_WITH_LOCATIONS_QUERY = gql`
  query getRegionsWithLocations {
    getRegions {
      id
      name
      locations {
        id
        name
      }
    }
  }
`;

// Query to get all regions data at once (dynamic regions)
export const GET_ALL_REGIONS_DONOR_ANALYTICS_QUERY = gql`
  query getAllRegionsDonorAnalytics {
    getAllRegionsDonorAnalytics {
      all {
        totalActiveDonors
        totalSingleDonors
        totalDoubleDonors
        totalNewDonors
        totalExperiencedDonors
        statusBreakdown {
          status
          count
        }
      }
      regionAnalytics {
        regionId
        regionName
        totalActiveDonors
        totalSingleDonors
        totalDoubleDonors
        totalNewDonors
        totalExperiencedDonors
        statusBreakdown {
          status
          count
        }
      }
      recentDonors {
        id
        name
        date
        days
        location
        status
        regionId
        regionName
      }
    }
  }
`;

// Flexible query that works with any region structure
export const GET_DONOR_ANALYTICS_FLEXIBLE_QUERY = gql`
  query getDonorAnalyticsFlexible($regionId: String) {
    getDonorAnalytics(regionId: $regionId) {
      regionId
      regionName
      totalActiveDonors
      totalSingleDonors
      totalDoubleDonors
      totalNewDonors
      totalExperiencedDonors
      statusBreakdown {
        status
        count
      }
      recentDonors {
        id
        name
        date
        days
        location
        status
      }
    }
  }
`;

// Legacy query kept for reference (can be removed later)
export const GET_DONOR_ANALYTICS_QUERY = gql`
  query getDonorAnalytics($input: GetDonorAnalyticsInput!) {
    getDonorAnalytics(input: $input) {
      regionId
      regionName
      totalActiveDonors
      totalSingleDonors
      totalDoubleDonors
      totalNewDonors
      totalExperiencedDonors
      statusBreakdown {
        status
        count
      }
    }
  }
`;

// Query to get phlebotomist stats for the Phlebotomist Dashboard
export const GET_PHLEBOTOMIST_STATS_QUERY = gql`
  query getPhlebotomistStats($input: GetPhlebotomistStatsInput!) {
    getPhlebotomistStats(input: $input) {
      # Phlebotomist Info
      phlebotomistId
      phlebotomistName
      phlebotomistEmail
      regionId
      regionName
      locationPreference
      assignedOfficeId
      assignedOfficeName

      # Donation Counts
      attemptedDonations
      successfulDonations
      pendingDonations
      singleDonors
      doubleDonors
      successfulSingleDonors
      successfulDoubleDonors

      # Expected Products
      expectedTotal
      expectedPRBC
      expectedPRBCSingle
      expectedPRBCDouble
      expectedFFPSingle
      expectedFFPDouble

      # Actual Products
      actualTotal
      actualPRBC
      actualPRBCSingle
      actualPRBCDouble
      actualFFPSingle
      actualFFPDouble

      # Special Metrics
      dblDonorsSglYield

      # Success Rates
      overallSuccessRate
      prbcSuccessRate
      sglDonorsSuccessRate
      dblDonorsSuccessRate

      # Goal/Team Metrics
      teamCount
      totalOfficeSuccessful
      donationSharePercent
      officeSharePercent

      # Discarded Products
      completelyFailedDonations
      completelyFailedDiscards {
        reason
        count
      }
      partiallyFailedDonations
      partiallyFailedDiscards {
        reason
        count
      }

      # Recent Donations
      recentDonations {
        id
        shortId
        date
        donorName
        donorId
        type
        approvedML
        actualML
        collectionSet
        veinSide
        donationUnit
        examinationNotes
        ineligibleReason
        expectedPRBC
        expectedFFPSingle
        expectedFFPDouble
        actualPRBC
        actualFFPSingle
        actualFFPDouble
        status
        phlebotomistName
        locationName
        regionName
        failureReasons
        products {
          type
          productId
          status
          orderId
          discardReason
        }
      }
    }
  }
`;

// Query to get phlebotomist trend stats for the Phlebotomist Dashboard trends mode
export const GET_PHLEBOTOMIST_TREND_STATS_QUERY = gql`
  query getPhlebotomistTrendStats($input: GetPhlebotomistTrendStatsInput!) {
    getPhlebotomistTrendStats(input: $input) {
      period
      periodStart
      periodEnd

      # Donation Counts
      attemptedDonations
      successfulDonations
      singleDonors
      doubleDonors

      # Expected Products
      expectedTotal
      expectedPRBC
      expectedFFPSingle
      expectedFFPDouble

      # Actual Products
      actualTotal
      actualPRBC
      actualFFPSingle
      actualFFPDouble

      # Special Metrics
      dblDonorsSglYield

      # Success Rates
      overallSuccessRate
      prbcSuccessRate
      sglDonorsSuccessRate
      dblDonorsSuccessRate
    }
  }
`;

// Query to get recruiter stats (recruited donors + active locations per recruiter)
export const GET_OPS_OVERVIEW_DB_SUMMARY_QUERY = gql`
  query getOpsOverviewDbSummary($input: GetOpsOverviewStatsInput!) {
    getOpsOverviewDbSummary(input: $input) {
      monthlyUnitsShipped
      prevMonthlyUnitsShipped
      totalProduced
      totalDiscarded
      prbcProduced
      prbcDiscarded
      ffpProduced
      ffpDiscarded
      topDiscardReasons {
        reason
        count
      }
      earliestSoldDate
      earliestProductDate
      unitsShippedBreakdown {
        prbcTotal
        prbcSgl
        prbcDbl
        ffpTotal
        ffpSgl
        ffpDbl
      }
      prevUnitsShippedBreakdown {
        prbcTotal
        ffpTotal
      }
      wastageRate
      prevWastageRate
      prevPrbcWastageRate
      prevFfpWastageRate
      wastageBreakdown {
        prbcRate
        ffpRate
        prbcSglRate
        prbcDblRate
        ffpSglRate
        ffpDblRate
      }
      inventoryTurnover
      prevInventoryTurnover
      prevPrbcTurnover
      prevFfpTurnover
      turnoverBreakdown {
        prbcRate
        ffpRate
        prbcSglRate
        prbcDblRate
        ffpSglRate
        ffpDblRate
      }
      reservationFillRate
    }
  }
`;

export const GET_OPS_OVERVIEW_WOO_SUMMARY_QUERY = gql`
  query getOpsOverviewWooSummary($input: GetOpsOverviewStatsInput!) {
    getOpsOverviewWooSummary(input: $input) {
      wooCommerceSalesCount
      prevWooCommerceSalesCount
      wooCommerceSalesTotal
      prevWooCommerceSalesTotal
      wooPrbcCount
      wooFfpSglCount
      wooFfpDblCount
      wooOtherCount
      prevWooPrbcCount
      prevWooFfpCount
    }
  }
`;

export const GET_OPS_OVERVIEW_SUMMARY_QUERY = gql`
  query getOpsOverviewSummary($input: GetOpsOverviewStatsInput!) {
    getOpsOverviewSummary(input: $input) {
      monthlyUnitsShipped
      prevMonthlyUnitsShipped
      unitsShippedBreakdown {
        prbcTotal
        prbcSgl
        prbcDbl
        ffpTotal
        ffpSgl
        ffpDbl
      }
      prevUnitsShippedBreakdown {
        prbcTotal
        ffpTotal
      }
      wastageRate
      prevWastageRate
      prevPrbcWastageRate
      prevFfpWastageRate
      wastageBreakdown {
        prbcRate
        ffpRate
        prbcSglRate
        prbcDblRate
        ffpSglRate
        ffpDblRate
      }
      inventoryTurnover
      prevInventoryTurnover
      prevPrbcTurnover
      prevFfpTurnover
      turnoverBreakdown {
        prbcRate
        ffpRate
        prbcSglRate
        prbcDblRate
        ffpSglRate
        ffpDblRate
      }
      reservationFillRate
      wooCommerceSalesCount
      prevWooCommerceSalesCount
      wooCommerceSalesTotal
      prevWooCommerceSalesTotal
      wooPrbcCount
      wooFfpSglCount
      wooFfpDblCount
      wooOtherCount
      prevWooPrbcCount
      prevWooFfpCount
    }
  }
`;

export const GET_OPS_OVERVIEW_STATS_QUERY = gql`
  query getOpsOverviewStats($input: GetOpsOverviewStatsInput!) {
    getOpsOverviewStats(input: $input) {
      monthlyUnitsShipped
      prevMonthlyUnitsShipped
      unitsShippedBreakdown {
        prbcTotal
        prbcSgl
        prbcDbl
        ffpTotal
        ffpSgl
        ffpDbl
      }
      prevUnitsShippedBreakdown {
        prbcTotal
        ffpTotal
      }
      wastageRate
      prevWastageRate
      prevPrbcWastageRate
      prevFfpWastageRate
      wastageBreakdown {
        prbcRate
        ffpRate
        prbcSglRate
        prbcDblRate
        ffpSglRate
        ffpDblRate
      }
      inventoryTurnover
      prevInventoryTurnover
      prevPrbcTurnover
      prevFfpTurnover
      turnoverBreakdown {
        prbcRate
        ffpRate
        prbcSglRate
        prbcDblRate
        ffpSglRate
        ffpDblRate
      }
      reservationFillRate
      wooCommerceSalesCount
      prevWooCommerceSalesCount
      wooCommerceSalesTotal
      prevWooCommerceSalesTotal
      wooTotalUnits
      prevWooTotalUnits
      shippedUnitEntries {
        productShortId
        productStatus
        woocommerceProductId
        woocommerceCategoryId
        woocommerceOrderId
        donorName
        ownerName
        location
        regionName
        createdAt
        updatedAt
        donationDate
      }
      turnoverEntries {
        unitType
        inQty
        outQty
        turnoverPct
        regionName
      }
      discardedUnitEntries {
        productShortId
        woocommerceProductId
        woocommerceCategoryId
        discardReason
        discardNotes
        donorName
        ownerName
        location
        regionName
        createdAt
        donationDate
      }
      wooPrbcCount
      wooFfpSglCount
      wooFfpDblCount
      wooOtherCount
      prevWooPrbcCount
      prevWooFfpCount
      wooLocationBreakdown {
        locationName
        prbcCount
        ffpSglCount
        ffpDblCount
        otherCount
        revenue
      }
      wooOrders {
        orderId
        orderNumber
        status
        dateCreated
        total
        customerName
        company
        shipLocationName
        shippingClassSlug
        splitType
        lineItems {
          id
          name
          productId
          quantity
          sku
          price
          meta {
            key
            value
          }
        }
      }
    }
  }
`;

export const GET_REGION_RECRUIT_STATS_QUERY = gql`
  query getRegionRecruitStats($input: GetRegionRecruitStatsInput!) {
    getRegionRecruitStats(input: $input) {
      entries {
        regionId
        regionName
        recruitedDonorCount
        prevRecruitedDonorCount
        activeLocationCount
        totalLocationCount
      }
    }
  }
`;

export const GET_RECRUITER_STATS_QUERY = gql`
  query getRecruiterStats($input: GetRecruiterStatsInput!) {
    getRecruiterStats(input: $input) {
      entries {
        userId
        firstName
        lastName
        regionId
        regionName
        recruitedDonorCount
        activeLocationCount
        totalAssignedLocationCount
        prevRecruitedDonorCount
        prevActiveLocationCount
      }
      unassignedLocationCount
      unassignedLocations {
        id
        name
        regionName
      }
    }
  }
`;

// Query to get per-location stats for a specific recruiter
export const GET_RECRUITER_LOCATION_STATS_QUERY = gql`
  query getRecruiterLocationStats($input: GetRecruiterLocationStatsInput!) {
    getRecruiterLocationStats(input: $input) {
      totalLocationCount
      entries {
        locationId
        locationName
        regionName
        totalActiveDonors
        latestDonation
        recruitedCount
        prevRecruitedCount
        totalUnits
        unitDonorCount
        successfulDonations
        attemptedDonations
        prevAttemptedDonations
        prevSuccessfulDonations
        donationEventCount
        alltimeUnits
        alltimeEventCount
        lastMonthUnits
        prevMonthUnits
        prevDonationEventCount
        pendingDonorCount
        sglPercent
        pendingAvgDays
        daysSinceLastRegistration
        prevPeriodUnits
        sglDonorCount
        dblDonorCount
        firstTimeDonationCount
        retiredDonorCount
        totalUnitsSinceInception
        firstDonationDate
        retiringIn30Count
        retiringIn60Count
        retiringIn90Count
        avgDaysAppliedToActive
        avgDaysPendingToApproved
        avgDaysActiveToRetired
        avgDaysPendingToArchived
        totalDonorPool
        activeDonors90DaysAgo
        lastNewDonorName
        lastNewDonorDate
        stalledDonorsJson
      }
    }
  }
`;

// Query to get newly recruited donors for a specific recruiter
export const GET_RECRUITER_DONORS_QUERY = gql`
  query getRecruiterDonors($input: GetRecruiterDonorsInput!) {
    getRecruiterDonors(input: $input) {
      donors {
        donorId
        donorShortId
        donorName
        ownerFullName
        ownerShortId
        bloodTypeDisplay
        status
        lastDonationDate
        sglDbl
        totalDonations
        periodUnits
        location
        registeredAt
        ownerPhone
        ownerEmail
        ownerCity
        ownerState
        ownerContactPreferences
        ownerAssignedLocations
        approvedDonationML
        weight
        breed
        region
      }
      total
    }
  }
`;

// Query to get recruiter leaderboard for the Recruiter Dashboard
export const GET_RECRUITER_LEADERBOARD_QUERY = gql`
  query getRecruiterLeaderboard($input: GetRecruiterLeaderboardInput!) {
    getRecruiterLeaderboard(input: $input) {
      entries {
        userId
        firstName
        lastName
        regionId
        newDonorCount
      }
    }
  }
`;

export const GET_SHAREPOINT_LIST_ITEMS_QUERY = gql`
  query getSharepointListItems($listId: String!) {
    getSharepointListItems(listId: $listId) {
      listId
      displayName
      totalFetched
      totalCount
      items
      sharepointEnabled
    }
  }
`;
